import React, { useState, useEffect } from 'react';
import GlobalHeader from './GlobalHeader';
import certificatesData from '../data/certificates.json';
import theme from '../config/theme';

interface Certificate {
    _id: { $oid: string };
    certId: string;
    studentFullName: string;
    studentEmail: string;
    coureName: string;
    courseId: string;
    dateIssue: { $date: string };
    image: {
        medium: {
            url: string;
        };
        large?: {
            url: string;
        };
    };
}

interface CertifiedTestersPageProps {
    user: any;
    onSignOut: () => void;
    onSignIn: () => void;
}

const CertifiedTestersPage: React.FC<CertifiedTestersPageProps> = ({ user, onSignOut, onSignIn }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCertificates, setFilteredCertificates] = useState<Certificate[]>([]);
    const [displayedCertificates, setDisplayedCertificates] = useState<Certificate[]>([]);
    const [page, setPage] = useState(1);
    const itemsPerPage = 12;

    useEffect(() => {
        // Initial load
        const sorted = [...(certificatesData as any[])].sort((a, b) => {
            return new Date(b.dateIssue.$date).getTime() - new Date(a.dateIssue.$date).getTime();
        });
        setFilteredCertificates(sorted);
    }, []);

    useEffect(() => {
        const results = (certificatesData as any[]).filter(cert => {
            const searchLower = searchTerm.toLowerCase();
            return (
                cert.studentFullName.toLowerCase().includes(searchLower) ||
                cert.studentEmail.toLowerCase().includes(searchLower) ||
                cert.certId.toLowerCase().includes(searchLower)
            );
        }).sort((a, b) => {
            return new Date(b.dateIssue.$date).getTime() - new Date(a.dateIssue.$date).getTime();
        });
        setFilteredCertificates(results);
        setPage(1); // Reset to first page on search
    }, [searchTerm]);

    useEffect(() => {
        const start = 0;
        const end = page * itemsPerPage;
        setDisplayedCertificates(filteredCertificates.slice(start, end));
    }, [page, filteredCertificates]);

    const handleLoadMore = () => {
        setPage(prev => prev + 1);
    };

    const handleCardClick = (certId: string) => {
        window.location.hash = `#/certified-testers/${certId}`;
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${theme.colors.bg} 0%, ${theme.colors.bgAlt} 100%)`,
            paddingTop: '80px' // For fixed header
        }}>
            <GlobalHeader
                user={user}
                onSignOut={onSignOut}
                onSignIn={onSignIn}
                activeTab="certified-testers"
            />

            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '40px 20px',
            }}>
                {/* Header Section */}
                <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                    <h1 style={{
                        fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                        fontWeight: '800',
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        marginBottom: '16px',
                        letterSpacing: '-0.02em'
                    }}>
                        Certified QA Engineers
                    </h1>
                    <p style={{
                        fontSize: '1.125rem',
                        color: '#64748b',
                        maxWidth: '600px',
                        margin: '0 auto',
                        lineHeight: '1.6'
                    }}>
                        Verify certificates and discover our talented community of certified automation testing professionals.
                    </p>
                </div>

                {/* Search Bar */}
                <div style={{
                    maxWidth: '600px',
                    margin: '0 auto 60px',
                    position: 'relative'
                }}>
                    <input
                        type="text"
                        placeholder="Search by name, email, or certificate ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '16px 24px 16px 56px',
                            fontSize: '16px',
                            borderRadius: '50px',
                            border: '1px solid rgba(203, 213, 225, 0.6)',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(12px)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            color: '#1e293b',
                            boxSizing: 'border-box'
                        }}
                        onFocus={(e) => {
                            e.target.style.boxShadow = '0 8px 30px rgba(99, 102, 241, 0.15)';
                            e.target.style.borderColor = '#818cf8';
                        }}
                        onBlur={(e) => {
                            e.target.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.05)';
                            e.target.style.borderColor = 'rgba(203, 213, 225, 0.6)';
                        }}
                    />
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#64748b"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                            position: 'absolute',
                            left: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none'
                        }}
                    >
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                </div>

                {/* Results Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '32px',
                    marginBottom: '40px'
                }}>
                    {displayedCertificates.map((cert) => (
                        <div
                            key={cert.certId}
                            onClick={() => handleCardClick(cert.certId)}
                            style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '20px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                                border: '1px solid rgba(226, 232, 240, 0.8)',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)';
                            }}
                        >
                            {/* Image Container */}
                            <div style={{
                                position: 'relative',
                                paddingTop: '75%', // 4:3 Aspect Ratio
                                overflow: 'hidden',
                                backgroundColor: '#f1f5f9'
                            }}>
                                <img
                                    src={cert.image?.medium?.url || cert.image?.large?.url}
                                    alt={`${cert.studentFullName} Certificate`}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transition: 'transform 0.5s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                />
                            </div>

                            {/* Content */}
                            <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <h3 style={{
                                    margin: '0 0 8px 0',
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    color: '#1e293b',
                                    lineHeight: '1.4'
                                }}>
                                    {cert.studentFullName}
                                </h3>
                                <p style={{
                                    margin: '0 0 16px 0',
                                    fontSize: '14px',
                                    color: '#64748b',
                                    fontWeight: '500'
                                }}>
                                    {cert.coureName}
                                </p>

                                <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{
                                        fontSize: '12px',
                                        color: '#94a3b8',
                                        fontWeight: '500'
                                    }}>
                                        {new Date(cert.dateIssue.$date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                    </span>
                                    <span style={{
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#4f46e5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        View Details
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M5 12h14M12 5l7 7-7 7" />
                                        </svg>
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Load More Button */}
                {displayedCertificates.length < filteredCertificates.length && (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <button
                            onClick={handleLoadMore}
                            style={{
                                padding: '12px 32px',
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '50px',
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#475569',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8fafc';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            Load More Certificates
                        </button>
                    </div>
                )}

                {/* No Results State */}
                {displayedCertificates.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                        <h3 style={{ fontSize: '20px', color: '#1e293b', marginBottom: '8px' }}>No certificates found</h3>
                        <p style={{ color: '#64748b' }}>Try adjusting your search terms</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CertifiedTestersPage;
